import { useQuery } from 'react-query'
import { makeCoreGetFileRequest, makeCoreGetRequest } from 'utils/api'
import {
  downloadCSV,
  downloadZip,
  formatAndDownloadResponse,
  formatAndPrintPDFResponse,
  formatPayloadDate,
  handleQueryError
} from 'utils/helpers'
import {
  AccompanyingNoteCollectorReportsItem,
  CollectingCompanyTableItem,
  CompanyLocation,
  OperationType,
  SimpleItem,
  WasteCategoryOption,
  WMSDocumentType
} from './interfaces'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toJobTitlesData } from './serializers'
import { parseBlobErrorResponse } from 'utils/formatting'

export const getCollectingCompany = (
  showNotification: Function,
  companyId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['collecting-company', companyId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(`/waste-management-system/company/${companyId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea companiei colectoare.',
          showNotification
        ),
      enabled: !!companyId && shouldRefetchItem
    }
  )
}

export const getCollectingCompanyOptions = (showNotification: Function) => {
  return useQuery(
    ['collecting-companies-options'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/company?page=-1')
      return data.list?.map((item: SimpleItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea companiilor colectoare.',
          showNotification
        )
    }
  )
}

export const getCollectingCompanyOptionsByCategory = (
  showNotification: Function,
  category: AutocompleteOption
) => {
  return useQuery(
    ['collecting-companies-options-by-category', category],
    async () => {
      const url = toTableGETUrl({
        url: '/waste-management-system/company',
        page: -1,
        tableFilters: [
          { name: 'categories', value: [category] },
          { name: 'currentLocation', value: true }
        ]
      })
      const data = await makeCoreGetRequest(url)
      return data.list?.map((item: CollectingCompanyTableItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea companiilor colectoare.',
          showNotification
        ),
      enabled: !!category?.value
    }
  )
}

export const getCollectingCompaniesByCategory = (
  showNotification: Function,
  category: AutocompleteOption
) => {
  return useQuery(
    ['collecting-companies-by-category', category],
    async () => {
      const url = toTableGETUrl({
        url: '/waste-management-system/company',
        page: -1,
        tableFilters: [
          { name: 'categories', value: [category] },
          { name: 'currentLocation', value: true }
        ]
      })
      const data = await makeCoreGetRequest(url)
      return data.list
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea companiilor colectoare.',
          showNotification
        ),
      enabled: !!category?.value
    }
  )
}

export const getWasteCategoryOptions = (showNotification: Function) => {
  return useQuery(
    ['waste-categories'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/category')
      return data.map((item: WasteCategoryOption) => ({
        label: item.name,
        value: item.id,
        code: item.code,
        wasteReportCollectionType: item.wasteReportCollectionType
      }))
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea listei de categorii de deseuri.',
          showNotification
        )
    }
  )
}

export const getDailyReport = (
  showNotification: Function,
  reportId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['daily-report', reportId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(`/waste-management-system/daily-report/${reportId}`)
      return result
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea raportului zilnic.',
          showNotification
        ),
      enabled: !!reportId && shouldRefetchItem
    }
  )
}

export const getDailyReportDetails = (
  showNotification: Function,
  reportId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['daily-report-details', reportId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(
        `/waste-management-system/daily-report/details/${reportId}`
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea detaliilor raportului zilnic.',
          showNotification
        ),
      enabled: !!reportId && shouldRefetchItem
    }
  )
}

export const getDailyReportMetiReports = (
  showNotification: Function,
  reportId: number,
  enabled: boolean
) => {
  return useQuery(
    ['daily-report-meti-reports', reportId],
    async () => {
      const result = await makeCoreGetRequest(`/waste-management-system/meti-reports/${reportId}`)
      return (Array.isArray(result) ? result : result?.list || []) as SimpleItem[]
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea rapoartelor METI.',
          showNotification
        ),
      enabled: !!reportId && enabled
    }
  )
}

export const getDailyReportDocumentPreview = (
  showNotification: Function,
  reportId: number,
  shouldRefetchItem: boolean,
  hasDocument: boolean
) => {
  return useQuery(
    ['daily-report-document-preview', reportId, shouldRefetchItem],
    async () => {
      const url = `/waste-management-system/document?report-type=DAILY&report-id=${reportId}&document-type=DAILY_REPORT`
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      const contentType = String(response.headers['content-type'] ?? '')
      return { blob: response.data as Blob, contentType }
    },
    {
      onError: async (error: any) => {
        // No document attached to this report -> skip notification.
        if (error?.response?.status === 404) return

        const errorMessage = await parseBlobErrorResponse(error)
        showNotification({
          message: `A aparut o eroare la previzualizarea documentului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: !!reportId && shouldRefetchItem && hasDocument,
      retry: false
    }
  )
}

export const getDailyReportCurrentUserSignature = (
  showNotification: Function,
  reportId: number,
  employeeId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['daily-report-current-user-signature', reportId, employeeId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(
        `/waste-management-system/daily-report/signatures/${reportId}/${employeeId}`,
        {
          responseType: 'blob'
        }
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea semnaturii utilizatorului pentru raportul zilnic.',
          showNotification
        ),
      enabled: !!reportId && !!employeeId && shouldRefetchItem
    }
  )
}

export const getLocationTypesOptions = (showNotification: Function) => {
  return useQuery(
    ['location-types'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/locations/types')
      return data.map((item: SimpleItem) => ({
        label: item.name,
        value: item.id
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'There was an error fetching location types.', showNotification)
    }
  )
}

export const getActiveDailyReportsByCategory = (
  showNotification: Function,
  category: any,
  isEdit: boolean
) => {
  return useQuery(
    ['active-daily-reports-by-category', category],
    async () => {
      const data = await makeCoreGetRequest(
        `/waste-management-system/daily-report/unprocessed?wasteCategoryCode=${category.code}`
      )

      return data
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea listei de rapoarte zilnice.',
          showNotification
        ),
      enabled: !!category?.value && !isEdit
    }
  )
}

export const getJobTitleOptions = (showNotification: Function, storeADId: number) => {
  return useQuery(
    ['job-title-options', storeADId],
    async () => {
      const data = await makeCoreGetRequest(
        `/waste-management-system/locations/job-titles/${storeADId}`
      )

      return toJobTitlesData(data)
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea listei de functii.',
          showNotification
        ),
      enabled: !!storeADId
    }
  )
}

export const getLocationOptions = (showNotification: Function) => {
  return useQuery(
    ['waste-locations'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/locations')
      return data.map((item: CompanyLocation) => ({
        label: item.name,
        value: item.metiCode
      }))
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea listei de locatii.',
          showNotification
        )
    }
  )
}

export const getDocumentTypesOptions = (showNotification: Function) => {
  return useQuery(
    ['waste-document-types'],
    async () => {
      const data = await makeCoreGetRequest(
        '/waste-management-system/document/types?reportType=ACCOMPANY_NOTE'
      )
      return data.map((item: WMSDocumentType) => ({
        label: item.description,
        value: item.code,
        multiselect: item.multiselect
      }))
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea listei de tipuri de documente.',
          showNotification
        )
    }
  )
}

export const printDailyReportPDF = (showNotification: Function, reportId: number) => {
  return useQuery(
    ['print-daily-report-PDF', reportId],
    async () => {
      return await makeCoreGetRequest(`/waste-management-system/daily-report/pdf/${reportId}`, {
        responseType: 'blob'
      })
    },
    {
      onError: async error => {
        const errorMessage = await parseBlobErrorResponse(error)

        showNotification({
          message: `A aparut o eroare la deschiderea de print preview a fisierului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: !!reportId
    }
  )
}

export const downloadReportCsv = (showNotification: Function, url: string) => {
  return useQuery(
    ['download-daily-report-csv', url],
    async () => {
      const data = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      return downloadCSV(data)
    },
    {
      onError: async error => {
        const errorMessage = await parseBlobErrorResponse(error)

        showNotification({
          message: `A aparut o eroare la descarcarea fisierului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: false
    }
  )
}

export const printCollectorReportPDF = (showNotification: Function, reportId: number) => {
  return useQuery(
    ['print-collector-report-PDF', reportId],
    async () => {
      return await makeCoreGetRequest(
        `/waste-management-system/waste-report-collection-company/pdf/${reportId}`,
        {
          responseType: 'blob'
        }
      )
    },
    {
      onError: async error => {
        const errorMessage = await parseBlobErrorResponse(error)

        showNotification({
          message: `A aparut o eroare la deschiderea de print preview a fisierului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: !!reportId
    }
  )
}

export const getCollectorReport = (
  showNotification: Function,
  reportId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['collector-report', reportId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(
        `/waste-management-system/waste-report-collection-company/${reportId}`
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'A aparut o eroare la obtinerea raportului.', showNotification),
      enabled: !!reportId && shouldRefetchItem
    }
  )
}

export const getWasteReportCollectionCompanyOptions = (
  showNotification: Function,
  companyId: number,
  isEdit: boolean
) => {
  return useQuery(
    ['waste-report-collection-company', companyId],
    async () => {
      const result = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/waste-accompanying-note/unprocessed-reports',
          page: -1,
          tableFilters: [
            // { name: 'currentLocation', value: true },
            { name: 'collectionCompanyId', value: companyId }
          ]
        })
      )

      return result.wasteReports.map((report: AccompanyingNoteCollectorReportsItem) => ({
        label: report.name,
        value: report.id,
        weight: report.weight
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'A aparut o eroare la obtinerea raportului.', showNotification),
      enabled: !!companyId && !isEdit
    }
  )
}

export const getExternalArticlesReport = (
  showNotification: Function,
  reportId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['external-articles-report', reportId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(
        `/waste-management-system/external-articles/${reportId}`
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea raportului de produse externe.',
          showNotification
        ),
      enabled: !!reportId && shouldRefetchItem
    }
  )
}

export const printAccompanyingNotePDF = (showNotification: Function, reportId: number) => {
  return useQuery(
    ['print-accompanying-note-PDF', reportId],
    async () => {
      return await makeCoreGetRequest(
        `/waste-management-system/waste-accompanying-note/pdf/${reportId}`,
        {
          responseType: 'blob'
        }
      )
    },
    {
      onError: async error => {
        const errorMessage = await parseBlobErrorResponse(error)

        showNotification({
          message: `A aparut o eroare la deschiderea de print preview a fisierului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: !!reportId
    }
  )
}

export const getAccompanyingNote = (
  showNotification: Function,
  reportId: number,
  shouldRefetchItem: boolean
) => {
  return useQuery(
    ['accompanying-note', reportId, shouldRefetchItem],
    async () => {
      const result = await makeCoreGetRequest(
        `/waste-management-system/waste-accompanying-note/${reportId}`
      )
      return result
    },
    {
      onError: error =>
        handleQueryError(error, 'A aparut o eroare la obtinerea avizului.', showNotification),
      enabled: !!reportId && shouldRefetchItem
    }
  )
}

export const getOperationTypesOptions = (showNotification: Function) => {
  return useQuery(
    ['operation-types-options'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/operation-types')
      return data.map((item: OperationType) => ({
        label: item.description,
        value: item.name
      }))
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea tipurilor de operatii.',
          showNotification
        )
    }
  )
}

export const getLegalEntitiesOptions = (showNotification: Function) => {
  return useQuery(
    ['wms-legal-entities-options'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/locations/legal-entities')
      return data.map((item: string) => ({
        label: item,
        value: item
      }))
    },
    {
      onError: error =>
        handleQueryError(error, 'A aparut o eroare la obtinerea societatilor.', showNotification)
    }
  )
}

export const bulkDownload = (
  showNotification: Function,
  locationIds: string,
  startDate: Date,
  endDate: Date,
  cb: Function
) => {
  return useQuery(
    ['bulk-download', locationIds, startDate, endDate],
    async () => {
      let url = `/waste-management-system/document/bulk-download?fromDate=${encodeURIComponent(
        formatPayloadDate(startDate)
      )}&toDate=${encodeURIComponent(formatPayloadDate(endDate))}`

      if (locationIds) {
        url += `&metiIds=${locationIds}`
      }

      const data = await makeCoreGetFileRequest(url, {
        responseType: 'arraybuffer'
      })

      cb && cb()

      return downloadZip(data, `download_${Date.now()}.zip`)
    },
    {
      onError: error =>
        handleQueryError(error, 'A aparut o eroare la bulk download.', showNotification),
      enabled: false
    }
  )
}

export const printExternalArticlesReportPDF = (showNotification: Function, reportId: number) => {
  return useQuery(
    ['print-external-articles-report-PDF', reportId],
    async () => {
      return await makeCoreGetRequest(
        `/waste-management-system/external-articles/${reportId}/download`,
        {
          responseType: 'blob'
        }
      )
    },
    {
      onError: async error => {
        const errorMessage = await parseBlobErrorResponse(error)

        showNotification({
          message: `A aparut o eroare la deschiderea de print preview a fisierului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: !!reportId
    }
  )
}

export const getCurrentLocationInfo = (showNotification: Function) => {
  return useQuery(
    ['current-location-info'],
    async () => {
      const result = await makeCoreGetRequest('/waste-management-system/locations/current-location')
      return result
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea informatiilor despre locatia curenta.',
          showNotification
        )
    }
  )
}

export const downloadDocument = (
  showNotification: Function,
  reportType: string,
  reportId: number,
  documentType: string,
  cb?: Function
) => {
  return useQuery(
    ['download-document', reportType, reportId, documentType],
    async () => {
      let url = `/waste-management-system/document?report-type=${reportType}&report-id=${reportId}`
      if (documentType) {
        url += `&document-type=${documentType}`
      }

      const data = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })

      cb && cb()

      const contentType = String(data.headers['content-type'] ?? '')

      if (contentType.includes('image/')) {
        formatAndDownloadResponse(data, `${reportType}_${documentType}_${Date.now()}.png`)
      } else if (contentType === 'application/pdf') {
        return formatAndDownloadResponse(data, `${reportType}_${documentType}_${Date.now()}.pdf`)
      } else if (contentType === 'application/zip') {
        return downloadZip(data, `${reportType}_${documentType}_${Date.now()}.zip`)
      }
    },
    {
      onError: async error => {
        const errorMessage = await parseBlobErrorResponse(error)

        showNotification({
          message: `A aparut o eroare la descarcarea fisierului. ${errorMessage}`,
          type: 'error'
        })
      },
      enabled: !!reportId
    }
  )
}

export const getCurrentCollectingCompanies = (showNotification: Function) => {
  return useQuery(
    ['current-collecting-companies'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/company/location')
      return data
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea companiilor colectoare.',
          showNotification
        )
    }
  )
}

export const getCompanyLocationDetails = (showNotification: Function) => {
  return useQuery(
    ['company-location-details'],
    async () => {
      const data = await makeCoreGetRequest('/waste-management-system/company/location/details')
      return data
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea detaliilor locatiei companiei.',
          showNotification
        )
    }
  )
}

export const getNonHandoverDailyReports = (
  showNotification: Function,
  wasteCompanyName: string
) => {
  return useQuery(
    ['non-handover-daily-reports', wasteCompanyName],
    async () => {
      const data = await makeCoreGetRequest(
        `/waste-management-system/daily-report/waste-company-non-handover?wasteCompanyName=${encodeURIComponent(
          wasteCompanyName
        )}`
      )
      return data
    },
    {
      onError: error =>
        handleQueryError(
          error,
          'A aparut o eroare la obtinerea rapoartelor zilnice nepredabile.',
          showNotification
        ),
      enabled: !!wasteCompanyName
    }
  )
}
